import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { useApolloClient } from 'react-apollo';

import {
  ALBUM_PRIVACY,
  ALBUM_TYPE,
  ALBUM_UPLOAD_PERMISSIONS,
} from '../../../../../constants/albums';
import { isAlbum, isAlbumIndividual } from '../../../../../util/album';
import Alert from '../../../../../components/global/Alert';
import Button from '../../../../../components/global/Button';
import FremiumEventTutorialActionModal from './components/FremiumEventTutorialActionModal';
import { getCreateWizardAlbums } from '../FreemiumEventCreateWizard/helpers';
import useFreemiumEventTutorialWizard from './useFreemiumEventTutorialWizard';
import { isAlbumFolderOwner, isAlbumOwner } from '../../../../../util/account';
import { CONTRIBUTOR_UPLOAD_ACCESS } from '../../../../../constants/contributors';
import useIsNativeWebView from '../../../../../services/native-bridge/hooks/useIsWebView';
import EventAddonItemUpsellModal from '../../../../../components/global/EventAddonItemUpsell/modal';

const propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string,
    purchased_event_addon_types: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onCheckoutSuccess: PropTypes.func,
  configuration: PropTypes.shape({
    stepKeys: PropTypes.string,
    activeStepKey: PropTypes.arrayOf(PropTypes.string),
  }),
  onStepChanged: PropTypes.func,
  modalOnly: PropTypes.bool,
};

const FreemiumEventTutorialWizard = ({
  entity,
  onCheckoutSuccess,
  configuration,
  onStepChanged,
  modalOnly = false,
}) => {
  const isNativeWebView = useIsNativeWebView(configuration?.stepKeys);

  const {
    modalOpen,
    setModalOpen,
    activeStepIndex,
    steps,
    stepProps,
    onStep,
    user,
    onSkip,
    isComplete,
    submitting,
    clientStoreTutorialComplete,
  } = useFreemiumEventTutorialWizard({
    entity,
    configuration,
    onCheckoutSuccess,
    onStepChanged,
  });

  const apolloClient = useApolloClient();

  if (
    clientStoreTutorialComplete ||
    isComplete ||
    entity?.album_type !== ALBUM_TYPE.EVENT ||
    (isAlbum(entity) && !isAlbumIndividual(entity)) || // Only return wizard for ind. albums
    !(isAlbumOwner(user, entity) || isAlbumFolderOwner(user, entity))
  ) {
    return null;
  }

  const activeStep = steps[activeStepIndex];

  const onValidate = async values => {
    return activeStep.validate?.(values, apolloClient) ?? {};
  };

  return (
    <>
      <Alert type="info" className="freemium-event-tutorial-alert">
        <div className="d-flex flex-column">
          <span className="text-uppercase text-xxs text-extra-bold text-primary mb-1">
            Complete your setup ({activeStepIndex + 1}/{steps.length})
          </span>
          <p className="mb-0 text-bold">{activeStep.alertLabel}</p>
        </div>

        <div>
          {activeStep.skipBtnLabel && (
            <Button
              className="freemium-event-tutorial-alert-btn mr-1"
              btnSize="sm"
              btnOutline
              onClick={() => {
                onSkip();
              }}
              disabled={submitting}
              {...(activeStep.alertSkipBtnProps ?? {})}
            >
              {activeStep.skipBtnLabel}
            </Button>
          )}
          {activeStep.actionBtnLabel && (
            <Button
              disabled={submitting}
              className="freemium-event-tutorial-alert-btn"
              btnSize="sm"
              onClick={() => {
                setModalOpen(true);
              }}
              {...(activeStep.alertActionBtnProps ?? {})}
            >
              {activeStep.actionBtnLabel}
            </Button>
          )}
        </div>
      </Alert>
      <Formik
        initialValues={{
          // Weddings related specifically:
          albums: getCreateWizardAlbums({ ignoreWeddingDayAlbum: true }),
          privacy: entity.privacy ?? ALBUM_PRIVACY.PUBLIC,
          // Individual albums access settings only:
          password: entity.password ?? '',
          invite_only_access: entity.invite_only_access ?? false,
          // Uploading
          permission: entity.auto_contributor_settings
            ? ALBUM_UPLOAD_PERMISSIONS.ANYONE
            : ALBUM_UPLOAD_PERMISSIONS.INVITEES,
          require_review:
            entity.auto_contributor_settings?.upload_access ===
            CONTRIBUTOR_UPLOAD_ACCESS.REVIEW,
          // Social:
          hash_code: entity.hash_code,
          watermark_config: entity.watermark_config || {},
        }}
        onSubmit={onStep}
        validate={onValidate}
        validateOnBlur={false}
        validateOnChange={false}
        validateOnMount={false}
        render={({
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          handleSubmit,
        }) => {
          return (
            <Fragment>
              <FremiumEventTutorialActionModal
                isOpen={modalOnly || modalOpen}
                steps={steps}
                stepProps={stepProps}
                activeStepIndex={activeStepIndex}
                values={values}
                touched={touched}
                errors={errors}
                handleChange={handleChange}
                handleBlur={handleBlur}
                handleSubmit={handleSubmit}
                onSkip={onSkip}
                onClose={
                  isNativeWebView
                    ? undefined
                    : () => {
                        setModalOpen(false);
                      }
                }
                submitting={submitting}
              />
              <EventAddonItemUpsellModal />
            </Fragment>
          );
        }}
      />
    </>
  );
};

FreemiumEventTutorialWizard.propTypes = propTypes;

export default FreemiumEventTutorialWizard;
